import type { WebAppClusterDto } from '@terra/shared-contracts-external-network';

export const WEB_APP_CLUSTER_DECISIONS = ['create', 'link', 'skip'] as const;

export type WebAppClusterDecision = (typeof WEB_APP_CLUSTER_DECISIONS)[number];

export type WebAppConfirmSeed = {
  decisions: Record<string, WebAppClusterDecision>;
  linkChoices: Record<string, string>;
};

export type WebAppConfirmSummary = {
  createCount: number;
  linkCount: number;
  skipCount: number;
};

export function isWebAppClusterDecision(
  value: string,
): value is WebAppClusterDecision {
  return WEB_APP_CLUSTER_DECISIONS.some((decision) => decision === value);
}

export function isReviewableWebAppCluster(cluster: WebAppClusterDto): boolean {
  return cluster.verdict === 'CUSTOM' && !cluster.application;
}

export function countReviewableWebAppClusters(
  clusters: WebAppClusterDto[],
): number {
  return clusters.filter(isReviewableWebAppCluster).length;
}

export function mergePreviewWithPersistedClusters({
  preview,
  persisted,
}: {
  preview?: WebAppClusterDto[];
  persisted?: WebAppClusterDto[];
}): WebAppClusterDto[] {
  if (!preview?.length) {
    return persisted ?? [];
  }

  const persistedByKey = new Map(
    (persisted ?? []).map((cluster) => [cluster.similarityKey, cluster]),
  );

  return preview.map((cluster) => {
    const persistedCluster = persistedByKey.get(cluster.similarityKey);

    if (!persistedCluster?.application) {
      return cluster;
    }

    return {
      ...cluster,
      applicationId: persistedCluster.applicationId,
      application: persistedCluster.application,
      suggestedApplication: null,
    };
  });
}

export function seedWebAppConfirmState(
  clusters: WebAppClusterDto[],
): WebAppConfirmSeed {
  return clusters.filter(isReviewableWebAppCluster).reduce<WebAppConfirmSeed>(
    (seed, cluster) => {
      const suggestedId = cluster.suggestedApplication?.id;

      if (suggestedId) {
        seed.decisions[cluster.similarityKey] = 'link';
        seed.linkChoices[cluster.similarityKey] = suggestedId;

        return seed;
      }

      seed.decisions[cluster.similarityKey] = 'create';

      return seed;
    },
    { decisions: {}, linkChoices: {} },
  );
}

export function summarizeWebAppConfirmDecisions(
  decisions: Record<string, WebAppClusterDecision>,
): WebAppConfirmSummary {
  return Object.values(decisions).reduce<WebAppConfirmSummary>(
    (summary, decision) => {
      if (decision === 'create') {
        summary.createCount += 1;

        return summary;
      }

      if (decision === 'link') {
        summary.linkCount += 1;

        return summary;
      }

      summary.skipCount += 1;

      return summary;
    },
    { createCount: 0, linkCount: 0, skipCount: 0 },
  );
}

export function toConfirmDetectWebAppsPayload({
  decisions,
  linkChoices,
  enableAutoCreate,
}: {
  decisions: Record<string, WebAppClusterDecision>;
  linkChoices: Record<string, string>;
  enableAutoCreate: boolean;
}): {
  selectedSimilarityKeys: string[];
  links: Array<{ similarityKey: string; applicationId: string }>;
  enableAutoCreate: boolean;
} {
  const selectedSimilarityKeys = Object.entries(decisions)
    .filter(([, decision]) => decision !== 'skip')
    .map(([similarityKey]) => similarityKey);

  return {
    enableAutoCreate,
    selectedSimilarityKeys,
    links: selectedSimilarityKeys.flatMap((similarityKey) => {
      if (decisions[similarityKey] !== 'link') {
        return [];
      }

      const applicationId = linkChoices[similarityKey];

      if (!applicationId) {
        return [];
      }

      return [{ similarityKey, applicationId }];
    }),
  };
}

export function countWaitingMatchedWebAppClusters(
  clusters: WebAppClusterDto[],
): number {
  return clusters.filter(
    (cluster) =>
      cluster.verdict === 'CUSTOM' &&
      !cluster.application &&
      Boolean(cluster.suggestedApplication),
  ).length;
}

export function hasIncompleteWebAppLink({
  decisions,
  linkChoices,
}: {
  decisions: Record<string, WebAppClusterDecision>;
  linkChoices: Record<string, string>;
}): boolean {
  return Object.entries(decisions).some(
    ([similarityKey, decision]) =>
      decision === 'link' && !linkChoices[similarityKey],
  );
}

export function isDetectWebAppsConfirmSubmitDisabled({
  confirmMode,
  clustersLoading,
  previewFailed,
  reviewableCount,
  hasIncompleteLink,
}: {
  confirmMode: 'review' | 'enable' | null;
  clustersLoading: boolean;
  previewFailed: boolean;
  reviewableCount: number;
  hasIncompleteLink: boolean;
}): boolean {
  if (clustersLoading || previewFailed || hasIncompleteLink) {
    return true;
  }

  if (confirmMode === 'enable') {
    return false;
  }

  return reviewableCount === 0;
}
